import React from "react";
import Header from "../../components/Header";
import History from "../../components/History";
import rand from "random-key";
import ReactGA from "react-ga4";

import "./Home.css";
import { onValue, child, getDatabase, ref, set } from "firebase/database";
import { getAuth, onAuthStateChanged, signOut, User } from "firebase/auth";
import { IconContext } from "react-icons";
import { CiLogin, CiLogout } from "react-icons/ci";
import { useNavigate, Link } from "react-router-dom";
import { PageProps, HomePageState, NavigateFunction } from "../../types";


interface HomePageWrapperProps extends PageProps {
    navigate: NavigateFunction;
}

// Create a wrapper component to use hooks
function HomePageWrapper(props: HomePageWrapperProps): React.ReactElement {
    const navigate = useNavigate();
    return <HomePage {...props} navigate={navigate} />;
}

class HomePage extends React.Component<HomePageWrapperProps, HomePageState> {
    private scrollHideTimeout: ReturnType<typeof setTimeout> | null = null;
    private handleScroll: (() => void) | null = null;

    constructor(props: HomePageWrapperProps) {
        super(props);
        this.state = {
            key: rand.generate(10),
            num: 0,
            login: false,
            isPrivate: false
        };
        this.loginRender = this.loginRender.bind(this);
        this.logout = this.logout.bind(this);
        this.login = this.login.bind(this);
    }

    componentDidMount = (): void => {
        ReactGA.initialize("G-GC90QV1WJF");
        ReactGA.send({ hitType: "pageview", page: "/Home" });
        const dbRef = ref(getDatabase());
        onValue(child(dbRef, `code-sessions/`), (s) => {
            const data = s.val();
            this.setState({ num: data ? Object.keys(data).length : 0 });
        });

        const auth = getAuth();
        onAuthStateChanged(auth, (user: User | null) => {
            if (user) {
                this.setState({ login: true });
            } else {
                this.setState({ login: false });
            }
        });

        document.body.classList.add("homepage-scroll");
        this.handleScroll = (): void => {
            document.body.classList.add("homepage-scrolling");
            if (this.scrollHideTimeout) clearTimeout(this.scrollHideTimeout);
            this.scrollHideTimeout = setTimeout(() => {
                document.body.classList.remove("homepage-scrolling");
                this.scrollHideTimeout = null;
            }, 1000);
        };
        window.addEventListener("scroll", this.handleScroll, { passive: true });
    };

    componentWillUnmount(): void {
        if (this.handleScroll) {
            window.removeEventListener("scroll", this.handleScroll);
        }
        if (this.scrollHideTimeout) clearTimeout(this.scrollHideTimeout);
        document.body.classList.remove("homepage-scroll", "homepage-scrolling");
    }

    // This function creates a new entry in our database when the user hits the 'Let's go' button
    letsGo = (): void => {
        const auth = getAuth();
        const user = auth.currentUser;
        const db = getDatabase();
        const sessionId = this.state.key;
        const initialContent = '# Welcome to your Interview Sandbox! \n\n# 1 - Just share this link with another person \n# \t  to get them on the same page.\n# 2 - Once they join, you\'ll see the call button.\n# 3 - The code, code output, and drawings are synced\n#     in real time between users.\n# 4 - Select between Javascript, Python, Ruby, Swift,\n#     Java, Go, Kotlin, PHP, R, and Scala.\n\n# Try running the code below to see the ouput!\n\nprint("hello world")';
        const createdon = new Date().toLocaleString();
        const language = "Python";
        const language_id = 71;
        const mode = "python";

        if (user) {
            const userId = user.uid;
            // For authenticated users, create a private sandbox
            set(ref(db, `private-sandboxes/${userId}/${sessionId}`), {
                content: initialContent,
                createdon,
                language,
                language_id,
                mode,
                user: userId
            });
            // Also add to user-sessions for history
            set(ref(db, `user-sessions/${userId}/${sessionId}`), {
                createdon,
                language,
                language_id,
                mode
            });
            this.setState({ isPrivate: true });
        } else {
            // For non-authenticated users, create a public sandbox
            set(ref(db, `code-sessions/${sessionId}`), {
                content: initialContent,
                createdon,
                language,
                language_id,
                mode
            });
            this.setState({ isPrivate: false });
        }
        window.location.href = `/box/${sessionId}`;
    };

    logout(): void {
        const auth = getAuth();
        signOut(auth).then(() => {
            console.log("logout");
        }).catch((error) => {
            console.error("Logout error:", error);
        });
    }

    login(): void {
        this.props.navigate("/login");
    }

    loginRender(): React.ReactElement {
        if (this.state.login === true) {
            return (
                <div className="loginBtn" title="logout" onClick={this.logout}>
                    <IconContext.Provider value={{
                        size: "1.35em",
                        style: {
                            paddingRight: "0.2rem",
                            marginTop: "0.1rem"
                        }
                    }}>
                        <CiLogout />
                    </IconContext.Provider>
                </div>
            );
        } else {
            return (
                <div className="loginBtn" title="login" onClick={this.login}>
                    <IconContext.Provider value={{
                        size: "1.35em",
                        style: {
                            paddingRight: "0.2rem",
                            marginTop: "0.1rem"
                        }
                    }}>
                        <CiLogin />
                    </IconContext.Provider>
                </div>
            );
        }
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const highlightClass = darkMode ? "highlight-dark" : "highlight";
        const buttonDivClass = darkMode ? "buttonDiv-dark" : "buttonDiv";
        const btnClass = darkMode ? "btn letsGo-dark" : "btn letsGo";

        return (
            <>
                <Header
                    extras={this.state.num ? `Total ${this.state.num}+ Shares` : null}
                    darkMode={darkMode}
                />
                {this.loginRender()}
                {this.state.login && <History darkMode={darkMode} dashboardOnly />}
                <div className={`homepage ${darkMode ? "homepage-dark" : ""}`}>
                    <div className="homepage-landing">
                        <section className="homepage-hero">
                            <p className="homepage-eyebrow">Your Engineers Have Better Things to Build</p>
                            <h1 className="homepage-title">
                                Stop losing your best engineers to interviews.
                            </h1>
                            <p className="homepage-subtitle">
                                Every technical screen costs 4–6 hours of senior engineering time. Interview Sandbox handles structured evaluations with AI — so your team ships product instead of running interview loops. Transparent, explainable scores your hiring managers can trust.
                            </p>
                            <div className={buttonDivClass}>
                                <button className={btnClass} onClick={this.letsGo}>
                                    Let's go
                                </button>
                            </div>
                        </section>

                        <section className="homepage-social-proof">
                            <div className="homepage-social-proof-inner">
                                <div className="homepage-social-proof-item">
                                    <span className="homepage-social-proof-value">6000+</span>
                                    <span className="homepage-social-proof-label">Interview Sessions</span>
                                </div>
                                <div className="homepage-social-proof-divider" />
                                <div className="homepage-social-proof-item">
                                    <span className="homepage-social-proof-value">$5</span>
                                    <span className="homepage-social-proof-label">Per interview</span>
                                </div>
                                <div className="homepage-social-proof-divider" />
                                <div className="homepage-social-proof-item">
                                    <span className="homepage-social-proof-value">20 min</span>
                                    <span className="homepage-social-proof-label">Average interview</span>
                                </div>
                                <div className="homepage-social-proof-divider" />
                                <div className="homepage-social-proof-item">
                                    <span className="homepage-social-proof-value">3 free</span>
                                    <span className="homepage-social-proof-label">No card required</span>
                                </div>
                            </div>
                        </section>
                    </div>

                    <section className="homepage-cli">
                        <h2 className="homepage-section-title">Get your data from the CLI</h2>
                        <p className="homepage-section-desc">
                            Devs and agents can fetch interviews, candidate status, and evaluations — no dashboard required. Use <code>--json</code> for scriptable output.
                        </p>
                        <div className={`cli-terminal ${darkMode ? "cli-terminal-dark" : ""}`}>
                            <div className="cli-terminal-header">
                                <span className="cli-terminal-dot" />
                                <span className="cli-terminal-dot" />
                                <span className="cli-terminal-dot" />
                                <span className="cli-terminal-title">scoring-report.log</span>
                            </div>
                            <div className="cli-terminal-body">
                                <div className="cli-line cli-prompt">
                                    <span className="cli-prompt-symbol">$</span> sandbox evaluate --candidate "Jane Smith" --interview abc123
                                </div>
                                <div className="cli-line cli-output">Evaluating against rubric: Senior Frontend Engineer</div>
                                <div className="cli-line cli-score">Problem solving ......... 8/10 · "Broke the problem into subproblems before coding"</div>
                                <div className="cli-line cli-score">Code quality ............ 7/10 · "Clean variable naming, missed one edge case"</div>
                                <div className="cli-line cli-score">System design ........... 9/10 · "Drew clear architecture, explained trade-offs"</div>
                                <div className="cli-line cli-score">Communication ........... 8/10 · "Explained reasoning while coding"</div>
                                <div className="cli-line cli-success">✓ Bias audit: PASSED</div>
                                <div className="cli-line cli-note">Scoring based on code output + technical reasoning only.</div>
                                <div className="cli-line cli-note">Accent, speech patterns, and behavioral signals excluded.</div>
                                <div className="cli-line cli-spacer" />
                                <div className="cli-line cli-comment"># For JSON output (agents, scripts):</div>
                                <div className="cli-line cli-prompt">
                                    <span className="cli-prompt-symbol">$</span> sandbox evaluate --candidate "Jane Smith" --interview abc123 --json
                                </div>
                                <div className="cli-line cli-spacer" />
                                <div className="cli-line cli-comment"># List interviews, then get candidate status:</div>
                                <div className="cli-line cli-prompt">
                                    <span className="cli-prompt-symbol">$</span> sandbox list
                                </div>
                                <div className="cli-line cli-prompt">
                                    <span className="cli-prompt-symbol">$</span> sandbox status --interview abc123
                                </div>
                                <div className="cli-line cli-spacer" />
                                <div className="cli-line cli-comment"># Configure API key (from Settings → API Keys):</div>
                                <div className="cli-line cli-prompt">
                                    <span className="cli-prompt-symbol">$</span> sandbox config set api-key isk_your_key_here
                                </div>
                            </div>
                        </div>
                        <p className="homepage-cli-install">
                            <code>npm install -g interview-sandbox-cli</code>
                            {" · "}
                            <Link to="/docs/cli" className="homepage-cli-docs-link">Full documentation</Link>
                        </p>
                    </section>

                    <section className="homepage-why">
                        <p className="homepage-why-eyebrow">Why Teams Switch</p>
                        <h2 className="homepage-why-title">Give your engineers back the hours they're losing to interviews.</h2>
                        <div className="homepage-why-grid">
                            <div className="homepage-why-card">
                                <span className="homepage-why-icon">&#60;/&#62;</span>
                                <h3 className="homepage-why-card-title">75% fewer engineering hours</h3>
                                <p className="homepage-why-card-desc">Your senior engineers are spending 15–20 hours a week on technical screens. Interview Sandbox handles structured evaluations with AI so your team focuses on final-round conversations that actually matter.</p>
                            </div>
                            <div className="homepage-why-card">
                                <span className="homepage-why-icon">&#128197;</span>
                                <h3 className="homepage-why-card-title">Hire in weeks, not months</h3>
                                <p className="homepage-why-card-desc">Candidates get evaluated within days of applying. No more losing top talent to competitors while your pipeline crawls through six rounds over three months. Average time-to-hire drops to 2–3 weeks.</p>
                            </div>
                            <div className="homepage-why-card">
                                <span className="homepage-why-icon">&#9998;</span>
                                <h3 className="homepage-why-card-title">Explainable, fair scoring</h3>
                                <p className="homepage-why-card-desc">Every score comes with a plain-English explanation tied to your rubric. No black-box AI. No penalizing accents or flagging nervousness — candidates get judged on engineering skills, nothing else.</p>
                            </div>
                            <div className="homepage-why-card">
                                <span className="homepage-why-icon">&#36;</span>
                                <h3 className="homepage-why-card-title">Mid-market pricing</h3>
                                <p className="homepage-why-card-desc">Starts at $15K/year. Enterprise competitors charge $35K+ for the same capabilities. You get the same quality interviews without the procurement gauntlet.</p>
                            </div>
                        </div>
                    </section>

                    <section className="homepage-stats">
                        <div className="homepage-stat">
                            <span className="homepage-stat-value">75%</span>
                            <span className="homepage-stat-label">Fewer engineering hours on interviews</span>
                        </div>
                        <div className="homepage-stat">
                            <span className="homepage-stat-value">2–3 wk</span>
                            <span className="homepage-stat-label">Time-to-hire, down from 6–12 weeks</span>
                        </div>
                        <div className="homepage-stat">
                            <span className="homepage-stat-value">100%</span>
                            <span className="homepage-stat-label">Explainable scores. Every single one.</span>
                        </div>
                    </section>

                    <section className="homepage-cta">
                        <h2 className="homepage-cta-title">
                            Your engineers should be building product, not running interview loops.
                        </h2>
                        <p className="homepage-cta-subtitle">
                            Interview Sandbox evaluates what candidates build and how they think — with transparent, explainable scores your hiring team can trust. Ship faster. Hire smarter.
                        </p>
                        <div className={buttonDivClass}>
                            <button className={btnClass} onClick={this.letsGo}>
                                Get started
                            </button>
                        </div>
                    </section>
                </div>
            </>
        );
    }
}

export default HomePageWrapper;
